/**
 * 👻 Phantom Nest — daily top-4 shadow-clone arena
 *
 * Owner canon (2026-09-23 ruleset + 2026-10-03 loot tables):
 *   • Fight 4 "Dark-{handle}" shadow clones of today's top-4 users.
 *   • 5 gates: Easy → Normal → Hero → Nightmare → Cosmic.
 *   • 5 tiers per gate. Clear tier → 1 Flame Box. Max 5 boxes/day.
 *   • Party 1–4. Up to 3 Glimmers. Forced Easy first.
 *   • Clearing a gate unlocks the next gate at the NEXT reset (25-day
 *     minimum to Cosmic).
 *   • Loot tables are server-authoritative — this page just reveals them.
 *
 * v1 scope (shipping): state machine + stat-roll combat. Full tile
 * formation visuals, smart combo AI, and Dark-clone portrait generator
 * are v2.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Skull, Flame, Shield, Users, Lock, CheckCircle2, Gift, X, Sparkles, UserPlus, UserMinus, DoorOpen, DoorClosed } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GATE_TONE = {
  easy:      { ring: "border-emerald-500/50", chip: "bg-emerald-900/40 text-emerald-200", glow: "0 0 32px rgba(16,185,129,0.4)" },
  normal:    { ring: "border-sky-500/50",     chip: "bg-sky-900/40 text-sky-200",         glow: "0 0 32px rgba(56,189,248,0.4)" },
  hero:      { ring: "border-amber-500/50",   chip: "bg-amber-900/40 text-amber-200",     glow: "0 0 32px rgba(251,191,36,0.5)" },
  nightmare: { ring: "border-fuchsia-500/50", chip: "bg-fuchsia-900/40 text-fuchsia-200", glow: "0 0 36px rgba(217,70,239,0.55)" },
  cosmic:    { ring: "border-cyan-400/60",    chip: "bg-cyan-900/40 text-cyan-200",       glow: "0 0 44px rgba(103,232,249,0.7)" },
};

// ── Shadow clone card ─────────────────────────────────────
function ShadowCard({ shadow, i }) {
  return (
    <div
      className="relative rounded-xl border border-violet-500/40 bg-gradient-to-br from-violet-950/70 via-black to-fuchsia-950/40 p-3 text-center overflow-hidden"
      data-testid={`phantom-nest-shadow-${i}`}
      style={{ boxShadow: "inset 0 0 20px rgba(167,139,250,0.15)" }}
    >
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(217,70,239,0.08) 0px, rgba(217,70,239,0.08) 1px, transparent 1px, transparent 3px)",
        }}
      />
      <div className="relative">
        <div
          className="text-4xl mb-1"
          style={{
            filter: "drop-shadow(0 0 10px rgba(167,139,250,0.6))",
            // Discovery-first: slight glitch distortion so even the icon
            // doesn't fully "give" the clone away.
            textShadow: "1px 0 rgba(217,70,239,0.6), -1px 0 rgba(103,232,249,0.4)",
          }}
        >
          {shadow.icon || "🐦‍🔥"}
        </div>
        <div className="text-[11px] font-bold text-violet-100 truncate">{shadow.handle}</div>
        <div className="text-[9px] uppercase tracking-widest text-violet-400/60 italic">
          ??? ???
        </div>
      </div>
    </div>
  );
}

// ── Gate ladder card ──────────────────────────────────────
function GateCard({ gate, tiersPerGate, onPickTier, selected }) {
  const tone = GATE_TONE[gate.id] || GATE_TONE.easy;
  const locked = !gate.unlocked;
  const complete = !!gate.complete;
  const nextTier = gate.next_tier;

  return (
    <div
      className={`relative rounded-xl border-2 ${tone.ring} bg-gradient-to-br from-black via-neutral-950 to-black p-4 ${locked ? "opacity-50" : ""}`}
      style={{ boxShadow: locked ? "none" : tone.glow }}
      data-testid={`phantom-nest-gate-${gate.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${tone.chip}`}>
            Gate {gate.order}
          </span>
          <span className="text-base font-black uppercase tracking-wider text-white">
            {locked ? "???" : gate.name}
          </span>
        </div>
        {complete && <CheckCircle2 size={16} className="text-emerald-400" />}
        {locked && <Lock size={14} className="text-neutral-500" />}
      </div>

      {/* Tier dots */}
      <div className="flex items-center gap-1" data-testid={`phantom-nest-gate-${gate.id}-tiers`}>
        {Array.from({ length: tiersPerGate }).map((_, i) => {
          const t = i + 1;
          const cleared = t <= gate.cleared;
          const current = !locked && t === nextTier;
          const canFight = !locked && current && !complete;
          return (
            <button
              key={t}
              type="button"
              disabled={!canFight}
              onClick={() => canFight && onPickTier(gate.id, t)}
              data-testid={`phantom-nest-tier-${gate.id}-${t}`}
              className={`relative w-9 h-9 rounded-md border-2 text-[11px] font-black transition ${
                cleared
                  ? "bg-emerald-500/20 border-emerald-500 text-emerald-200"
                  : current
                    ? `${selected?.gate_id === gate.id && selected?.tier === t ? "ring-2 ring-white" : ""} bg-fuchsia-600/30 border-fuchsia-400 text-fuchsia-100 hover:bg-fuchsia-500/50`
                    : "bg-neutral-900 border-neutral-800 text-neutral-600 cursor-not-allowed"
              }`}
            >
              {t}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Fight result modal ────────────────────────────────────
function FightResultModal({ result, onClose }) {
  if (!result) return null;
  const cleared = result.cleared;
  const loot = result.loot;
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="phantom-nest-fight-result"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative max-w-md w-full rounded-2xl border-2 p-6 bg-gradient-to-br ${
          cleared
            ? "border-emerald-500/60 from-emerald-950/90 via-black to-emerald-950/40"
            : "border-fuchsia-500/60 from-fuchsia-950/90 via-black to-purple-950/50"
        }`}
        style={{
          boxShadow: cleared
            ? "0 0 60px rgba(16,185,129,0.4)"
            : "0 0 60px rgba(217,70,239,0.4)",
        }}
      >
        <button
          onClick={onClose}
          data-testid="phantom-nest-fight-result-close"
          className="absolute top-3 right-3 text-neutral-400 hover:text-white"
        >
          <X size={20} />
        </button>

        <div className="text-center mb-4">
          <div className="text-5xl mb-2">{cleared ? "🏆" : "☠️"}</div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/80">
            {cleared ? "Tier Cleared" : "Shadow squad holds"}
          </div>
          <div className="text-2xl font-black text-white">
            {cleared ? "Flame Box Unlocked" : "Try Again"}
          </div>
        </div>

        <div className="rounded-lg bg-black/50 border border-neutral-800 p-3 text-xs text-neutral-300 italic mb-4" data-testid="phantom-nest-flavor">
          {cleared
            ? "The last shadow dissolves. A Flame Box falls at your feet."
            : "The shadow squad locks you out. They've seen this move before."}
        </div>

        {cleared && loot && (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-950/30 p-3 mb-3" data-testid="phantom-nest-loot">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2 font-bold">
              {loot.label}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {(loot.rewards || []).map((r) => (
                <div
                  key={r.key}
                  className="flex items-center gap-1.5 text-sm bg-black/40 rounded px-2 py-1"
                  data-testid={`phantom-nest-reward-${r.key}`}
                >
                  <span className="text-base">{r.icon}</span>
                  <div className="min-w-0">
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 truncate">{r.name}</div>
                    <div className="text-sm font-bold text-emerald-200">+{Number(r.amount).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
            {!!loot.chits_bonus && (
              <div className="mt-2 text-[11px] text-amber-300 font-bold">
                💰 Chits bonus triggered — +{Number(loot.chits_bonus).toLocaleString()} Chits
              </div>
            )}
          </div>
        )}

        {cleared && result.next_gate_unlocked && (
          <div className="rounded-lg border border-cyan-500/50 bg-cyan-950/30 p-3 text-center mb-3">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">Gate Cleared</div>
            <div className="text-sm font-bold text-white">
              Next gate <span className="uppercase">{result.next_gate_unlocked}</span> opens at the next daily reset.
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          data-testid="phantom-nest-fight-result-ok"
          className={`w-full py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition ${
            cleared
              ? "bg-emerald-500 hover:bg-emerald-400 text-black"
              : "bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
          }`}
        >
          {cleared ? "Collect" : "Regroup"}
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────
export default function PhantomNestPage() {
  const { user } = useAuth();
  const myUserId = user?.user_id;
  const [state, setState]       = useState(null);
  const [selected, setSelected] = useState(null);  // { gate_id, tier }
  const [fighting, setFighting] = useState(false);
  const [result, setResult]     = useState(null);
  // 🪶 Oct 2026 — helper reward opt-in (checkbox). Default: unchecked.
  const [wantsRewards, setWantsRewards] = useState(false);
  const [glimmers, setGlimmers] = useState(0);
  // 🎉 Party co-op
  const [session, setSession]   = useState(null);   // current session (host or helper)
  const [lobby, setLobby]       = useState([]);     // joinable open sessions
  const heartbeatRef            = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/phantom-nest/state`, { withCredentials: true });
      setState(data);
    } catch (e) {
      toast.error("Failed to load Phantom Nest state.");
    }
  };

  const loadLobby = async () => {
    try {
      const { data } = await axios.get(`${API}/phantom-nest/sessions`, { withCredentials: true });
      setLobby(data?.sessions || []);
    } catch (e) { /* silent — lobby is optional */ }
  };

  useEffect(() => { load(); loadLobby(); }, []);

  // 💓 Heartbeat loop — host pings every 45s so the session doesn't
  // auto-close while they're on the page. Helpers also poll session
  // state so they see tier advances in near-real-time.
  useEffect(() => {
    if (!session?.session_id) {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
      return;
    }
    const isHost = session.host_user_id === myUserId;
    heartbeatRef.current = setInterval(async () => {
      try {
        if (isHost) {
          await axios.post(`${API}/phantom-nest/session/${session.session_id}/heartbeat`, {}, { withCredentials: true });
        }
        const { data } = await axios.get(`${API}/phantom-nest/session/${session.session_id}`, { withCredentials: true });
        if (!data?.session_id) {
          // Session gone / closed
          setSession(null);
          toast.message("Party session ended.");
        } else {
          setSession(data);
          if (data.status !== "open") {
            setSession(null);
            toast.message(data.closed_reason === "host_left" ? "Host left — session over." : "Session closed.");
          }
        }
      } catch { /* keep rolling */ }
    }, 45000);
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current); };
  }, [session?.session_id, session?.host_user_id, myUserId]);

  const pickTier = (gate_id, tier) => {
    setSelected({ gate_id, tier });
  };

  const createSession = async () => {
    if (!selected) {
      toast.error("Pick a tier first.");
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/phantom-nest/session/create`,
        { gate_id: selected.gate_id, tier: selected.tier },
        { withCredentials: true },
      );
      if (data.ok && data.session) {
        setSession(data.session);
        toast.success(data.resumed ? "Resumed your session." : "Party room opened — invite helpers.");
        await loadLobby();
      } else if (data.message) {
        toast.error(data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open party room.");
    }
  };

  const joinSession = async (sid) => {
    try {
      const { data } = await axios.post(
        `${API}/phantom-nest/session/${sid}/join`, {}, { withCredentials: true },
      );
      if (data.ok && data.session) {
        setSession(data.session);
        toast.success("Joined the party. Help the host clear tiers for assist loot.");
      } else {
        toast.error(data.error || "Could not join.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Join failed.");
    }
  };

  const leaveSession = async () => {
    if (!session) return;
    try {
      await axios.post(`${API}/phantom-nest/session/${session.session_id}/leave`, {}, { withCredentials: true });
      setSession(null);
      await loadLobby();
      toast.message("Left the session.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Leave failed.");
    }
  };

  const kickHelper = async (targetId) => {
    if (!session) return;
    try {
      const { data } = await axios.post(
        `${API}/phantom-nest/session/${session.session_id}/kick`,
        { user_id: targetId },
        { withCredentials: true },
      );
      if (data.ok) setSession(data.session);
    } catch (e) { /* silent */ }
  };

  // 🪶 Roc rule 2026-10 — helper flips the "I want rewards" checkbox.
  // Unchecked = pure helping (default). Rotate freely; whatever the box
  // reads AT clear-time is what applies.
  const toggleRewards = async (checked) => {
    if (!session) return;
    setWantsRewards(checked);                       // optimistic UI
    try {
      const { data } = await axios.post(
        `${API}/phantom-nest/session/${session.session_id}/rewards`,
        { wants_rewards: !!checked },
        { withCredentials: true },
      );
      if (data?.ok && data.session) setSession(data.session);
      else if (data?.error) {
        toast.error(data.error);
        setWantsRewards(!checked);                  // rollback
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update reward opt-in.");
      setWantsRewards(!checked);                    // rollback
    }
  };

  // Keep local `wantsRewards` in sync with the server-held flag whenever
  // the session doc refreshes (e.g. after heartbeat polls or a rejoin).
  useEffect(() => {
    if (!session || !myUserId) return;
    const me = (session.helpers || []).find((h) => h.user_id === myUserId);
    if (me) setWantsRewards(!!me.wants_rewards);
  }, [session, myUserId]);

  const fight = async () => {
    if (fighting) return;
    // In a session the fight routes through the host endpoint so helpers
    // get credit. Outside a session, the solo endpoint is used.
    const inSession = session && session.host_user_id === myUserId;
    const inSoloMode = !session;
    if (!inSession && !inSoloMode) {
      toast.error("Only the host can fire the fight.");
      return;
    }
    const gate_id = inSession ? session.gate_id : selected?.gate_id;
    const tier    = inSession ? session.tier    : selected?.tier;
    if (!gate_id || !tier) return;
    setFighting(true);
    try {
      const strength = Math.max(1, Math.floor((state?.my_power || 1) / 2));
      const party = [
        { hero_id: "roc",  level: strength, equipped_gear_level: strength },
        { hero_id: "cari", level: strength, equipped_gear_level: strength },
      ];
      const url = inSession
        ? `${API}/phantom-nest/session/${session.session_id}/fight`
        : `${API}/phantom-nest/fight`;
      const body = inSession
        ? { party, glimmers }
        : { gate_id, tier, party, glimmers };
      const { data } = await axios.post(url, body, { withCredentials: true });
      if (data.ok === false && data.message) toast.error(data.message);
      setResult(data);
      if (data.session) setSession(data.session);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fight failed.");
    } finally {
      setFighting(false);
    }
  };

  if (!state) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-neutral-500" data-testid="phantom-nest-loading">
        Loading Phantom Nest…
      </div>
    );
  }

  const boxesLeft = Math.max(0, (state.boxes_cap || 5) - (state.boxes_today || 0));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200 pb-24" data-testid="phantom-nest-root">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-violet-900/40">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: "radial-gradient(circle at 30% 20%, rgba(217,70,239,0.3), transparent 50%), radial-gradient(circle at 70% 80%, rgba(103,232,249,0.2), transparent 55%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 py-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-violet-500/60 rounded-full mb-3">
            <Skull size={14} className="text-violet-300" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-violet-200">The Phantom Nest</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-[0.9] mb-3" style={{ fontFamily: "Anton, sans-serif" }}>
            FOUR SHADOWS. <span className="text-violet-400">FIVE GATES.</span>
          </h1>
          <p className="text-sm text-neutral-400 max-w-xl mx-auto">
            Today's top four champions are wearing someone else's face. Clear a tier to earn a Flame Box.
            Clear all five tiers of a gate and the next gate opens at the next reset.
          </p>
          <div className="mt-4 flex items-center justify-center gap-3 text-xs">
            <span className="px-2 py-1 rounded bg-violet-500/15 border border-violet-500/40 text-violet-200" data-testid="phantom-nest-boxes-today">
              <Gift size={11} className="inline -mt-0.5 mr-1" /> {state.boxes_today}/{state.boxes_cap} Flame Boxes today
            </span>
          </div>
          <div className="mt-3">
            <Link to="/arena" className="text-[10px] uppercase tracking-widest text-violet-300/70 hover:text-violet-200" data-testid="phantom-nest-back-arena">
              ← Back to Arena
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
        {/* ── Shadow roster ── */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Users size={14} className="text-violet-300" />
            <h2 className="text-[11px] uppercase tracking-widest font-bold text-violet-200">Today's Shadow Squad</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="phantom-nest-shadows">
            {(state.shadows || []).map((s, i) => <ShadowCard key={i} shadow={s} i={i} />)}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-1.5">
            Snapshot built {state.today_pt} · ranks by equipped gear · resets at 00:00 PT
          </div>
        </section>

        {/* ── Gates ladder ── */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Shield size={14} className="text-emerald-300" />
              <h2 className="text-[11px] uppercase tracking-widest font-bold text-emerald-200">Gate Ladder</h2>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">{state.tiers_per_gate} tiers per gate</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="phantom-nest-gates">
            {(state.gates || []).map((g) => (
              <GateCard
                key={g.id}
                gate={g}
                tiersPerGate={state.tiers_per_gate || 5}
                onPickTier={pickTier}
                selected={selected}
              />
            ))}
          </div>
        </section>

        {/* ── 🎉 Party room ── */}
        <section
          className="rounded-xl border-2 border-cyan-500/40 bg-gradient-to-br from-cyan-950/30 via-black to-sky-950/20 p-4"
          data-testid="phantom-nest-party"
        >
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Users size={14} className="text-cyan-300" />
              <h2 className="text-[11px] uppercase tracking-widest font-bold text-cyan-200">Party Room</h2>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              Helpers tick the box if they also want rewards · unchecked = pure helping
            </div>
          </div>

          {session ? (
            <div className="space-y-3" data-testid="phantom-nest-party-active">
              <div className="rounded-lg bg-black/50 border border-cyan-500/40 p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold">
                      {session.host_user_id === myUserId ? "You are hosting" : `Joined ${session.host_handle}'s room`}
                    </div>
                    <div className="text-sm font-black text-white">
                      {(session.gate_id || "easy").toUpperCase()} · Tier {session.tier}
                    </div>
                    <div className="text-[10px] text-neutral-500 mt-0.5" data-testid="phantom-nest-party-box-count">
                      Boxes earned this session · {session.boxes_awarded_to_host}
                    </div>
                  </div>
                  <button
                    onClick={leaveSession}
                    data-testid="phantom-nest-party-leave"
                    className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded border border-red-500/50 text-red-300 hover:bg-red-500/10 flex items-center gap-1"
                  >
                    <DoorOpen size={11} /> {session.host_user_id === myUserId ? "End room" : "Leave"}
                  </button>
                </div>
              </div>

              {/* Party roster — host + up to 3 helpers */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="phantom-nest-party-roster">
                <div className="rounded-lg border border-cyan-500/40 bg-cyan-950/30 p-2 text-center">
                  <div className="text-xl">👑</div>
                  <div className="text-[11px] font-bold text-cyan-100 truncate">{session.host_handle}</div>
                  <div className="text-[9px] uppercase tracking-widest text-cyan-400/80">Host</div>
                </div>
                {(session.helpers || []).map((h, i) => (
                  <div key={h.user_id} className="rounded-lg border border-sky-500/30 bg-sky-950/30 p-2 text-center relative" data-testid={`phantom-nest-party-helper-${i}`}>
                    <div className="text-xl">🧑‍🤝‍🧑</div>
                    <div className="text-[11px] font-bold text-sky-100 truncate">{h.handle}</div>
                    <div className="text-[9px] uppercase tracking-widest text-sky-400/80">Helper</div>
                    {session.host_user_id === myUserId && (
                      <button
                        onClick={() => kickHelper(h.user_id)}
                        data-testid={`phantom-nest-party-kick-${i}`}
                        title="Remove helper"
                        className="absolute top-1 right-1 text-red-400 hover:text-red-200"
                      >
                        <UserMinus size={11} />
                      </button>
                    )}
                  </div>
                ))}
                {/* Empty slots */}
                {Array.from({ length: Math.max(0, 3 - (session.helpers || []).length) }).map((_, i) => (
                  <div
                    key={`empty-${i}`}
                    className="rounded-lg border border-dashed border-neutral-700 bg-black/30 p-2 text-center"
                    data-testid={`phantom-nest-party-slot-empty-${i}`}
                  >
                    <div className="text-xl text-neutral-700">✧</div>
                    <div className="text-[11px] text-neutral-600 italic">Open slot</div>
                    <div className="text-[9px] uppercase tracking-widest text-neutral-700">—</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="phantom-nest-party-lobby">
              <button
                onClick={createSession}
                disabled={!selected}
                data-testid="phantom-nest-party-host"
                className={`w-full py-2 rounded-lg text-xs font-black uppercase tracking-widest transition ${
                  selected
                    ? "bg-cyan-600 hover:bg-cyan-500 text-white"
                    : "bg-neutral-900 border border-neutral-800 text-neutral-600 cursor-not-allowed"
                }`}
              >
                {selected ? `Open a Party Room (${selected.gate_id.toUpperCase()} T${selected.tier})` : "Pick a tier first to host"}
              </button>

              {/* Open sessions list */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                  Open rooms · tap to join
                </div>
                {lobby.length === 0 ? (
                  <div className="text-[11px] text-neutral-600 italic py-2" data-testid="phantom-nest-party-lobby-empty">
                    No open rooms right now.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {lobby.map((s) => (
                      <button
                        key={s.session_id}
                        onClick={() => joinSession(s.session_id)}
                        disabled={s.host_user_id === myUserId}
                        data-testid={`phantom-nest-party-lobby-row-${s.session_id}`}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded border border-neutral-800 bg-black/40 hover:border-cyan-500/60 hover:bg-cyan-950/30 transition text-left"
                      >
                        <div>
                          <div className="text-sm font-bold text-white">{s.host_handle}</div>
                          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                            {s.gate_id?.toUpperCase?.()} · Tier {s.tier} · {(s.helpers || []).length}/3 helpers
                          </div>
                        </div>
                        <UserPlus size={14} className="text-cyan-300" />
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={loadLobby}
                  data-testid="phantom-nest-party-lobby-refresh"
                  className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-neutral-300"
                >
                  ↻ Refresh
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ── Fight console ── */}
        <section
          className="rounded-xl border-2 border-fuchsia-500/50 bg-gradient-to-br from-fuchsia-950/40 via-black to-purple-950/30 p-4"
          data-testid="phantom-nest-fight-console"
        >
          <div className="flex items-center gap-2 mb-2">
            <Flame size={14} className="text-fuchsia-300" />
            <h2 className="text-[11px] uppercase tracking-widest font-bold text-fuchsia-200">Deploy</h2>
          </div>

          {(() => {
            const inSession = !!session;
            const isHost = inSession && session.host_user_id === myUserId;
            const activeGate = inSession ? session.gate_id : selected?.gate_id;
            const activeTier = inSession ? session.tier    : selected?.tier;
            if (!activeGate) {
              return (
                <div className="text-sm text-neutral-400 italic">
                  Pick an unlocked tier above to deploy. You'll fight all four shadow clones in order.
                </div>
              );
            }
            if (inSession && !isHost) {
              return (
                <div className="space-y-2 text-sm text-neutral-300" data-testid="phantom-nest-helper-wait">
                  <div>Standing by — the host will fire the next fight.</div>
                  {/* 🪶 Owner canon Oct 2026 — helpers check the box to also
                      earn rewards; unchecked = pure helping, no rewards.
                      Rotate freely; helper mode stays after personal boxes. */}
                  <label className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-cyan-300 cursor-pointer" data-testid="phantom-nest-helper-wants-rewards-label">
                    <input
                      type="checkbox"
                      data-testid="phantom-nest-helper-wants-rewards"
                      checked={!!wantsRewards}
                      onChange={(e) => toggleRewards(e.target.checked)}
                      className="accent-cyan-400"
                    />
                    I want rewards from this run (otherwise, pure helping)
                  </label>
                </div>
              );
            }
            return (
              <div className="space-y-3">
                {/* 🩹 Oct 2026 (owner canon) — PREP ROOM shows party
                    only. Gate info is INTENTIONALLY hidden here; it
                    surfaces on the leaderboard instead. */}
                {inSession && (
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300" data-testid="phantom-nest-prep-party">
                    Party of {1 + (session.helpers?.length || 0)} · ready to deploy
                  </div>
                )}

                {/* Glimmer picker */}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 flex items-center gap-1">
                    <Sparkles size={10} /> Glimmers (up to 3)
                  </div>
                  <div className="flex gap-1" data-testid="phantom-nest-glimmers">
                    {[0, 1, 2, 3].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setGlimmers(n)}
                        data-testid={`phantom-nest-glimmer-${n}`}
                        className={`flex-1 py-1.5 rounded text-[10px] font-black uppercase tracking-widest transition ${
                          glimmers === n
                            ? "bg-amber-500 text-black"
                            : "bg-neutral-900 border border-neutral-800 text-neutral-400 hover:bg-neutral-800"
                        }`}
                      >
                        {n}× {n > 0 && "✦"}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={fight}
                  disabled={fighting || boxesLeft === 0}
                  data-testid="phantom-nest-fight-btn"
                  className={`w-full py-3 rounded-lg text-xs font-black uppercase tracking-widest transition ${
                    fighting || boxesLeft === 0
                      ? "bg-neutral-900 text-neutral-600 cursor-not-allowed"
                      : "bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white hover:brightness-125 shadow-lg shadow-fuchsia-900/40"
                  }`}
                >
                  {boxesLeft === 0 ? "Daily cap reached · come back after reset" : fighting ? "Fighting…" : "⚔️ Start Fight"}
                </button>
              </div>
            );
          })()}
        </section>
      </div>

      <FightResultModal result={result} onClose={() => setResult(null)} />
    </div>
  );
}
